import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { offlineQueue } from '../utils/offlineQueue';

/**
 * Hook to monitor connection status and handle offline/online transitions
 */
export const useConnectionStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSupabaseConnected, setIsSupabaseConnected] = useState(true);
  const [lastSync, setLastSync] = useState(Date.now());
  const [queueStatus, setQueueStatus] = useState(offlineQueue?.getStatus());

  /**
   * Check Supabase connection health
   */
  const checkSupabaseConnection = useCallback(async () => {
    try {
      const { error } = await supabase?.from('workspaces')?.select('id')?.limit(1);
      
      setIsSupabaseConnected(!error);
      return !error;
    } catch (error) {
      setIsSupabaseConnected(false);
      return false;
    }
  }, []);

  /**
   * Handle online status change
   */
  const handleOnline = useCallback(async () => {
    setIsOnline(true);
    
    // Check Supabase connection
    const isConnected = await checkSupabaseConnection();
    
    if (isConnected) {
      // Process offline queue
      await offlineQueue?.processQueue();
      setLastSync(Date.now());
      setQueueStatus(offlineQueue?.getStatus());
    }
  }, [checkSupabaseConnection]);

  /**
   * Handle offline status change
   */
  const handleOffline = useCallback(() => {
    setIsOnline(false);
    setIsSupabaseConnected(false);
  }, []);

  /**
   * Manually trigger sync
   */
  const sync = useCallback(async () => {
    if (!isOnline) {
      throw new Error('Cannot sync while offline');
    }

    const isConnected = await checkSupabaseConnection();
    
    if (!isConnected) {
      throw new Error('Unable to connect to Supabase');
    }

    await offlineQueue?.processQueue();
    setLastSync(Date.now());
    setQueueStatus(offlineQueue?.getStatus());
  }, [isOnline, checkSupabaseConnection]);

  /**
   * Retry failed queue items
   */
  const retryFailed = useCallback(async () => {
    offlineQueue?.retryFailed();
    setQueueStatus(offlineQueue?.getStatus());
  }, []);

  /**
   * Clear failed queue items
   */
  const clearFailed = useCallback(() => {
    offlineQueue?.clearFailed();
    setQueueStatus(offlineQueue?.getStatus());
  }, []);

  useEffect(() => {
    // Add network status listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial connection check
    checkSupabaseConnection();

    // Periodic connection check (every 30 seconds)
    const interval = setInterval(checkSupabaseConnection, 30000);

    // Update queue status every 5 seconds
    const queueInterval = setInterval(() => {
      setQueueStatus(offlineQueue?.getStatus());
    }, 5000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
      clearInterval(queueInterval);
    };
  }, [handleOnline, handleOffline, checkSupabaseConnection]);

  return {
    isOnline,
    isSupabaseConnected,
    isConnected: isOnline && isSupabaseConnected,
    lastSync,
    queueStatus,
    sync,
    retryFailed,
    clearFailed,
    checkConnection: checkSupabaseConnection
  };
};